import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap } from '@react-google-maps/api';
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import { getGameDetails } from '../../apiservices/LocalStorageService';
import {
  fetchNextGeo,
  submitGeoPlay,
  fetchGameStatusStream,
} from '../../apiservices/axiosservice/gameservice';
import { useSse } from '../../sse';
import PlayerGeoRoundResult from './PlayerGeoRoundResult';
import GeoGameOver from '../gamelobby/GeoGameOver';
import GeoWaitingResult from '../gamelobby/GeoWaitingResult';
import './PlayerFlow.css';
import MusicToggle from '../gamelobby/MusicToggle';
import { getCurrentCounter } from '../../utils/timeCounter';
import { useLeaderboard } from '../../hooks/useLeaderboard';
import { sseManager } from '../../sse';

const mockTarget = { lat: 40.758, lng: -73.9855 }; // Times Square
const mockPlayers = [
  { name: 'Sara K.', initials: 'SK', color: '#66BB6A' },
  { name: 'You', initials: 'AT', color: '#F5A623' },
  { name: 'james92', initials: 'JA', color: '#66BB6A' },
];

const defaultMarker = new L.Icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// Animated marker: the standard pin plus two radiating "signal" rings.
// Uses a Leaflet divIcon so the pulse stays anchored to the geographic point
// and pans/zooms with the map.
const pulseIcon = L.divIcon({
  className: 'pgeo-pulse-wrap',
  html: `
    <span class="pgeo-pulse-ring"></span>
    <span class="pgeo-pulse-ring pgeo-pulse-ring--delay"></span>
    <img src="${markerIcon}" class="pgeo-pulse-pin" alt="" />
  `,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// Celebration melody played when the player confirms a guess.
// Served from the public/ folder (see project convention in MusicToggle).
// Must be invoked from a user gesture to satisfy browser autoplay policies.
const CELEBRATION_SOUND_SRC = '/audio/celebration_button_melody.mp3';

const playClickSound = () => {
  try {
    const audio = new Audio(CELEBRATION_SOUND_SRC);
    audio.volume = 0.6;
    // play() returns a promise that can reject under autoplay restrictions.
    audio.play().catch(() => {
      // ignore audio errors (e.g. autoplay policy)
    });
  } catch {
    // ignore audio errors
  }
};

const PlayerGeoPlay: React.FC = () => {
  const navigate = useNavigate();
  const gd = getGameDetails();
  const { gameId, playerId, gameType, geoAllowedTime } = gd || {};

  const [target, setTarget] = useState(mockTarget);
  const [timeLeft, setTimeLeft] = useState(geoAllowedTime || 60);
  const [currentRound, setCurrentRound] = useState<GeoCurrentRound>();
  const [guess, setGuess] = useState<{ lat: number; lng: number } | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [roundOver, setRoundOver] = useState(false);
  const [waitingForResult, setWaitingForResult] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [waveActive, setWaveActive] = useState(false);
  const submittedRef = useRef(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  const leafletRef = useRef<L.Map | null>(null);

  const geoSync = useSse<GeoCurrentRound>('geo-sync');
  const geoResult = useSse<GeoResult>('geo-result');
  const gameStatus = useSse<any>('game-status');
  const [geoResultData, setGeoResultData] = useState<GeoResult>();
  const leaderboardEntry = useLeaderboard(gameId);

  const [currentGameStatus, setCurrentGameStatus] = useState<GameStatusInfo>({
    gameId: '',
    gameType: 'geotype',
    totalRound: 9,
    status: 'IN_PROGRESS',
    gameTitle: '',
    scheduledGame: false,
    currentRound: 1,
  });

  useEffect(() => {
    if (gameId) sseManager.connect(gameId);
  }, [gameId]);
  // Fetch first round
  useEffect(() => {
    if (gameId && playerId) {
      fetchNextGeo({ gameId, playerId })
        .then((res) => {
          const d = res?.data ?? res;

          if (d?.targetLocation) {
            setTarget({
              lat: d.targetLocation.latitude,
              lng: d.targetLocation.longitude,
            });
            if (d.timeAllowed)
              setTimeLeft(getCurrentCounter(d.currentExpiryTime));
          }
        })
        .catch(() => {});
    }
  }, []);

  // SSE: new round from host
  useEffect(() => {
    if (geoSync?.targetLocation) {
      setTarget({
        lat: geoSync.targetLocation.latitude,
        lng: geoSync.targetLocation.longitude,
      });
      setTimeLeft(
        getCurrentCounter(geoSync.currentExpiryTime) || geoAllowedTime || 60
      );
      setCurrentRound(geoSync);
      setGuess(null);
      setRoundOver(false);
      setWaitingForResult(false);
      setGeoResultData(undefined); //reset result
      setShowMap(false);
      setWaveActive(false);
      submittedRef.current = false;
    }
  }, [geoSync]);

  //Sse Status update

  useEffect(() => {
    if (gameStatus?.status === 'FINISHED') setGameOver(true);
    if (gameStatus) setCurrentGameStatus(gameStatus);
  }, [gameStatus]);

  // Game status API Update
  useEffect(() => {
    fetchGameStatusStream(gameId)
      .then((res) => {
        setCurrentGameStatus(res.data);

        setGameOver(res.data.status === 'FINISHED');
      })
      .catch((error) => {
        console.log('Error fetching game status:', error);
      });
  }, []);

  // Timer
  useEffect(() => {
    if (timeLeft <= 0) {
      if (!submittedRef.current) handleSubmit();
      setWaitingForResult(true);
      return;
    }
    const t = setInterval(() => setTimeLeft((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  // Invalidate leaflet on show
  useEffect(() => {
    if (showMap && leafletRef.current)
      setTimeout(() => leafletRef.current?.invalidateSize(), 300);
  }, [showMap]);

  const handleSubmit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    try {
      await submitGeoPlay({
        gameId,
        playerId,
        gameType,
        timeTaken: Math.floor((geoAllowedTime || 60) - timeLeft),
        guessedLocation: guess
          ? { latitude: guess.lat, longitude: guess.lng }
          : { latitude: 0, longitude: 0 },
      } as any);
    } catch {}
  };

  // Confirm Guess: play click sound + radiating wave, then submit.
  const handleConfirmGuess = () => {
    playClickSound();
    setWaveActive(true);
    handleSubmit();
  };

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      const panorama = map.getStreetView();
      panorama.setPosition(target);
      panorama.setVisible(true);
      panorama.setOptions({
        addressControl: false,
        showRoadLabels: false,
        fullscreenControl: false,
        zoomControl: false,
      });
    },
    [target]
  );

  // Update street view when target changes
  useEffect(() => {
    if (mapRef.current) {
      const panorama = mapRef.current.getStreetView();
      panorama.setPosition(target);
    }
  }, [target]);

  //Geo result SSE - if round result received, show round result screen. If game result received, show game result screen
  const processedResultRef = useRef<number | null>(null);
  useEffect(() => {
    // if (geoResult && geoResult.currentRound !== processedResultRef.current) {
    if (geoResult) {
      // processedResultRef.current = geoResult.currentRound;
      setGeoResultData(geoResult);
      setWaitingForResult(false);
      setRoundOver(true);
    }
  }, [geoResult]);

  const MapClick: React.FC = () => {
    const map = useMap();
    useEffect(() => {
      leafletRef.current = map;
    }, [map]);
    useMapEvents({
      click(e) {
        if (timeLeft > 0 && !submittedRef.current)
          setGuess({ lat: e.latlng.lat, lng: e.latlng.lng });
      },
    });
    return null;
  };

  if (gameOver)
    return (
      <GeoGameOver
        onPlayAgain={() => navigate('/join', { state: { clearOldGame: true } })}
        leaderBoard={leaderboardEntry}
        gameInfo={currentGameStatus}
      />
    );
  if (roundOver)
    return (
      <PlayerGeoRoundResult
        showNextRound={false}
        gameInfo={currentGameStatus}
        data={geoResultData}
        onNextRound={() => {
          if (currentGameStatus.status === 'FINISHED') {
            setGameOver(true);
            return;
          }

          setGuess(null);
          setRoundOver(false);
          setShowMap(false);
          setWaveActive(false);
          submittedRef.current = false;
          setTimeLeft(geoAllowedTime || 60);
        }}
        leaderBoard={leaderboardEntry}
      />
    );

  if (waitingForResult)
    return (
      <GeoWaitingResult
        roundNumber={currentRound?.currentRound}
        totalRounds={currentGameStatus.totalRound}
      />
    );

  return (
    <div className="pgeo">
      <MusicToggle />
      {/* Street View */}
      <div className="pgeo__sv">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={target}
          zoom={14}
          onLoad={onMapLoad}
        />
      </div>

      {/* HUD: round + timer */}
      <div className="pgeo__hud">
        <span className="pgeo__round-label">
          Round {currentRound?.currentRound} / {currentGameStatus.totalRound}
        </span>
        <span className="pgeo__timer">{timeLeft}</span>
      </div>

      {/* Guess map toggle */}
      <div className="pgeo__map-toggle" onClick={() => setShowMap(!showMap)}>
        <FontAwesomeIcon icon={faMapLocationDot} />
        <span>{showMap ? 'Hide Map' : 'Make Guess'}</span>
      </div>

      {/* Expandable guess map */}
      {showMap && (
        <div className="pgeo__guess-map">
          <MapContainer
            center={[20, 0] as [number, number]}
            zoom={2}
            minZoom={2}
            style={{ height: '100%', width: '100%' }}
            maxBounds={
              [
                [-90, -180],
                [90, 180],
              ] as any
            }
            maxBoundsViscosity={1.0}
            worldCopyJump={false}
          >
            <TileLayer url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" />
            <MapClick />
            {guess && (
              <Marker
                position={[guess.lat, guess.lng] as [number, number]}
                icon={waveActive ? pulseIcon : defaultMarker}
              />
            )}
          </MapContainer>
          {guess && (
            <button
              disabled={waveActive}
              className="pgeo__confirm-btn"
              onClick={handleConfirmGuess}
            >
              {waveActive ? 'Confirmed' : 'Confirm Guess'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerGeoPlay;
